import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { supabase } from '../lib/supabase'
import useAuth from '../hooks/useAuth'
import { ErrorAlert } from '../components/ui'

const ROLE_HOME = {
  admin: '/admin',
  facilitator: '/facilitator',
  client: '/client',
}

export default function ChangePassword() {
  const { t } = useTranslation('auth')
  const { role } = useAuth()
  const navigate = useNavigate()
  const dashboardPath = ROLE_HOME[role] || '/admin'
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    if (password.length < 6) {
      setError(t('changePassword.passwordMinLength'))
      return
    }

    if (password !== confirmPassword) {
      setError(t('changePassword.passwordMismatch'))
      return
    }

    setLoading(true)

    try {
      const { error } = await supabase.auth.updateUser({ password })
      if (error) throw error
      setSuccess(true)
      setTimeout(() => navigate(dashboardPath, { replace: true }), 2000)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-md animate-fadeIn">
      <div className="mb-6">
        <Link
          to={dashboardPath}
          className="text-gray-500 hover:text-gray-700 font-semibold transition-colors"
        >
          {t('changePassword.backToDashboard')}
        </Link>
      </div>

      <div className="bg-white rounded-xl p-8 shadow-sm">
        <h2 className="font-display text-2xl font-medium text-gray-800 mb-6">
          {t('changePassword.title')}
        </h2>

        {success ? (
          <div className="text-center py-4">
            <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-gray-700 font-medium">{t('changePassword.successMessage')}</p>
            <p className="text-gray-500 text-sm mt-1">{t('changePassword.redirecting')}</p>
          </div>
        ) : (
          <>
            <ErrorAlert message={error} onDismiss={() => setError(null)} />

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block font-semibold text-gray-800 mb-2">{t('changePassword.newPasswordLabel')}</label>
                <input
                  type="password"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-base transition-all focus:outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-100"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('changePassword.newPasswordPlaceholder')}
                  required
                  autoFocus
                />
              </div>

              <div>
                <label className="block font-semibold text-gray-800 mb-2">{t('changePassword.confirmPasswordLabel')}</label>
                <input
                  type="password"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-base transition-all focus:outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-100"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t('changePassword.confirmPasswordPlaceholder')}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl font-semibold transition-all hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? t('changePassword.updating') : t('changePassword.updatePassword')}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  )
}
